import { Component, OnInit } from '@angular/core';
import { User } from '../../core/models/user.model';
import { normalizeUserRole, UserRole } from '../../core/enums/user-role.enum';
import { AuthService } from '../../core/services/auth.service';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { DashboardNavComponent } from './dashboard-nav.component';
import { DashboardStatsComponent } from './dashboard-stats.component';
import { ActivatedRoute } from '@angular/router';
import { FEATURE_KEYS } from '../../core/models/feature-flag.model';
import { FeatureFlagService } from '../../core/services/feature-flag.service';



@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, DashboardNavComponent, DashboardStatsComponent],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit {
  readonly companyLogo = 'assets/branding/logo.png';
  currentUser: User | null = null;
  isPlatformEmployee = false;
  isOperator = false;
  isAdmin = false;
  currentDate = Date.now();
  operatorBookingEnabled = false;
  availabilityMessage = '';
  
  // Dashboard stats
  stats = {
    totalBookings: 0,
    activeTrips: 0,
    totalVehicles: 0,
    totalRevenue: 0
  };

  constructor(
    private authService: AuthService,
    private featureFlags: FeatureFlagService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.loadCurrentUser();
    this.route.queryParamMap.subscribe(params => this.availabilityMessage = params.get('availabilityMessage') || '');
    this.featureFlags.effectiveFeatures$.subscribe(states => {
      this.operatorBookingEnabled = states[FEATURE_KEYS.operatorBookingManagement] === true;
    });
    this.featureFlags.loadEffectiveFeatures().subscribe({ error: () => undefined });
  }

  private loadCurrentUser(): void {
    this.currentUser = this.authService.getCurrentUser();
    const role = this.getCurrentUserRole();

    this.isAdmin = role === UserRole.ADMIN;
    this.isPlatformEmployee = role === UserRole.PLATFORM_EMPLOYEE;
    this.isOperator = role === UserRole.OPERATOR;

    // Load dashboard data based on role
    this.loadDashboardData();
  }

  private loadDashboardData(): void {
    // Load stats based on user role
    if (this.isAdmin || this.isPlatformEmployee) {
      this.loadPlatformEmployeeDashboard();
    } else if (this.isOperator) {
      this.loadOperatorDashboard();
    }
  }

  private loadPlatformEmployeeDashboard(): void {
    // Platform employees see cross-client operational stats
    // Implement API calls here
  }

  private loadOperatorDashboard(): void {
    // Operator sees only their organisation's stats
    // Implement API calls here
  }

  getDisplayName(): string {
    return this.currentUser?.userName || this.currentUser?.username || 'User';
  }

  getOrganisationName(): string {
    return this.currentUser?.organisation?.organisationName
      || this.currentUser?.organisation?.orgName
      || this.currentUser?.organisationName
      || '';
  }

  getHeaderLogo(): string {
    if (this.isOperator) {
      return this.currentUser?.organisation?.orgLogo
        || this.currentUser?.organisation?.organisationLogo
        || this.companyLogo;
    }

    return this.companyLogo;
  }

  useCompanyLogo(event: Event): void {
    const image = event.target as HTMLImageElement;
    if (image.src.endsWith(this.companyLogo)) {
      return;
    }

    image.src = this.companyLogo;
  }

  getCurrentUserRole(): UserRole {
    return this.currentUser
      ? normalizeUserRole(this.currentUser.userRoleName || this.currentUser.userRole || this.currentUser.userRoleID)
      : UserRole.PLATFORM_EMPLOYEE;
  }

  getPageTitle(): string {
    if (this.isAdmin) {
      return 'Admin Workspace';
    }

    if (this.isPlatformEmployee) {
      return 'Platform Operations';
    }

    if (this.isOperator) {
      return 'Operator Workspace';
    }

    return 'Passenger Portal';
  }

  getWelcomeMessage(): string {
    if (this.isAdmin) {
      return 'Manage users, clients, devices, fleet operations, bookings, and system-wide platform health.';
    }

    if (this.isPlatformEmployee) {
      return 'Monitor onboarding, devices, client operations, and fleet health from one workspace.';
    }

    if (this.isOperator) {
      return 'Manage routes, trips, vehicles, bookings, and live fleet movement for your organisation.';
    }

    return 'Find trips, manage bookings, and track journey information at a glance.';
  }
}
